// Libs
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
// UI-Components
import {
  OutletHeading,
  OutletSubHeading,
} from "../../../UI-Components/OutletWrapper";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogIcon,
  DialogTitle,
} from "../../../UI-Components/Dialog";
import SuccessIcon from "../../../UI-Components/SuccessIcon";
import { Field, Form, Formik } from "formik";
import { Label } from "../../../UI-Components/Label";
import { Input } from "../../../UI-Components/Input";
import { Button } from "../../../UI-Components/Button";
import Spinner from "../../../UI-Components/Spinner";
// Types
import { UpdateUserProfileForm } from "../../../Libs/Types/UpdateUserProfile";
import {
  FailureResponseWrapper,
  GetProfileAPI_Response,
  SuccessResponseWrapper,
} from "../../../Libs/Types/API-Responses";
// Endpoints
import {
  apiGetUserProfile,
  apiUpdateUserProfile,
} from "../../../Endpoints/Profile";

export default function UpdateUserProfile() {
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);

  // Fetching User Profile
  const {
    data: getUserData,
    isLoading: isUserProfileLoading,
    refetch: refetchUserProfile,
  } = useQuery({
    queryKey: ["/get-profile"],
    queryFn: apiGetUserProfile,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  let coveredData: SuccessResponseWrapper<GetProfileAPI_Response> =
    getUserData?.data;

  let initialValues: UpdateUserProfileForm = {
    firstName: coveredData?.data.first_name || "",
    lastName: coveredData?.data.last_name || "",
    phone: coveredData?.data.phone_number || "",
    email: coveredData?.data.email || "",
  };

  // Handle Dialog Close
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // API Response Handling
  const { mutateAsync: userUpdate, isPending: isUserUpdatePending } =
    useMutation({
      mutationFn: apiUpdateUserProfile,
      retry: 0,
      onSuccess: async () => {
        refetchUserProfile();
        setDialogOpen(true);
      },
      onError: (error: any) => {
        const errorData: FailureResponseWrapper = error.response.data;
        toast.error(errorData?.message ?? "Something Went Wrong", {
          duration: 3000,
        });
      },
      throwOnError: false,
    });

  // Handle User Profile Submit
  const handleUserProfileSubmit = async (values: UpdateUserProfileForm) => {
    await userUpdate({
      email: values.email,
      first_name: values.firstName,
      last_name: values.lastName,
      phone_number: values.phone,
    });
  };

  return (
    <>
      <div className="px-2">
        <OutletHeading>User Profile</OutletHeading>
        <OutletSubHeading className="mt-2">Fill the details</OutletSubHeading>

        {isUserProfileLoading ? (
          <div className="flex justify-center items-center gap-5">
            <span>Loading...</span>
            <Spinner className="w-6 h-6 border-primary-btn border-t-white" />
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleUserProfileSubmit}
          >
            <Form className="w-full">
              <div className="grid md:grid-cols-2 gap-x-4">
                <div className="my-1">
                  <Label
                    className="block text-primary text-xs my-2 font-semibold"
                    htmlFor="firstName"
                  >
                    First Name
                  </Label>
                  <Field
                    as={Input}
                    className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                    type="text"
                    id="firstName"
                    name="firstName"
                    // required
                    placeholder="Enter First Name"
                  />
                </div>
                <div className="my-1">
                  <Label
                    className="block text-primary text-xs my-2 font-semibold"
                    htmlFor="lastName"
                  >
                    Last Name
                  </Label>
                  <Field
                    as={Input}
                    className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                    type="text"
                    id="lastName"
                    name="lastName"
                    // required
                    placeholder="Enter Last Name"
                  />
                </div>
                <div className="my-1">
                  <Label
                    className="block text-primary text-xs my-2 font-semibold"
                    htmlFor="phone"
                  >
                    Phone Number
                  </Label>
                  <Field
                    as={Input}
                    className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                    type="text"
                    id="phone"
                    name="phone"
                    // required
                    placeholder="Enter Phone"
                  />
                </div>
                <div className="my-1">
                  <Label
                    className="block text-primary text-xs my-2 font-semibold"
                    htmlFor="email"
                  >
                    Email
                  </Label>
                  <Field
                    as={Input}
                    disabled
                    className="w-full py-2 border-primary text-title outline-none rounded transition bg-white"
                    type="email"
                    id="email"
                    name="email"
                  />
                </div>
              </div>
              <div className="mt-5">
                <Button type="submit" className="w-full">
                  {isUserUpdatePending ? (
                    <Spinner className="w-6 h-6" />
                  ) : (
                    "Update"
                  )}
                </Button>
              </div>
            </Form>
          </Formik>
        )}

        <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
          <DialogContent className="max-w-3xl">
            <DialogIcon>
              <SuccessIcon />
            </DialogIcon>
            <DialogHeader>
              <DialogDescription className="hidden">
                User profile updated successfully
              </DialogDescription>
              <DialogTitle>User profile updated successfully</DialogTitle>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
